import type { ComponentUnit, DoseUnit, FormularyInput, FormularyItem, FormularyKind } from "@vaulted/shared";
import { COMPONENT_UNITS, DOSE_UNITS, DOSE_UNIT_LABELS, formatDose, needsStrength, vialColor } from "@vaulted/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, post, put } from "../api";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Panel, SelectField, TextAreaField } from "../components/ui";
import { useSession } from "../session";
import type { CatalogResponse } from "./Catalog";
import { AddButton, RemoveButton } from "./settings/common";

const KIND_LABELS: Record<FormularyKind, string> = { medication: "Medications", fluid: "Fluids", supply: "Supplies" };

export const strengthLabel = (f: Pick<FormularyItem, "doseUnit" | "strengthPerMl" | "components">) =>
  f.doseUnit === "each"
    ? "Counted each"
    : f.doseUnit === "ml"
      ? f.components.length
        ? "Compounded, by mL"
        : "Measured in mL"
      : `${new Intl.NumberFormat("en-US").format(f.strengthPerMl ?? 0)} ${DOSE_UNIT_LABELS[f.doseUnit]}/mL`;

/** "B1 100 mg, B2 2 mg per mL" */
export const contentsLabel = (f: Pick<FormularyItem, "components">) =>
  f.components.length ? `${f.components.map((c) => `${c.name} ${formatDose(c.amountPerMl, c.unit)}`).join(", ")} per mL` : "";

export function FormularyTab({ data }: { data: CatalogResponse }) {
  const { can } = useSession();
  const manage = can("formulary.manage");
  const [editing, setEditing] = useState<FormularyItem | "new" | null>(null);
  const usedBy = (id: string) => data.items.filter((i) => i.status === "active" && i.recipe.some((l) => l.formularyItemId === id)).length;

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <p className="text-ink-soft">
        Medications, fluids, and supplies. Strength is set once here; build sheets work out the mL to draw from each recipe's dose.
      </p>
      {(["medication", "fluid", "supply"] as const).map((kind) => {
        const list = data.formulary.filter((f) => f.kind === kind);
        return (
          <Panel key={kind} className="overflow-x-auto">
            <div className="border-b border-mist px-4 py-3 font-semibold">{KIND_LABELS[kind]}</div>
            {list.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted">None yet.</p>
            ) : (
              <table className="table-base">
                <tbody>
                  {list.map((f) => (
                    <tr key={f.id} className="hover:bg-mist/50">
                      <td>
                        <span className="mr-2 inline-block size-2.5 rounded-full align-middle" style={{ background: vialColor(f.name, f.color) }} aria-hidden />
                        <button className="text-left font-semibold hover:text-brand disabled:hover:text-ink" disabled={!manage} onClick={() => setEditing(f)}>
                          {f.name}
                        </button>
                        {!f.active && (
                          <span className="ml-2">
                            <Badge tone="off">Inactive</Badge>
                          </span>
                        )}
                        {f.drugName && <div className="text-sm text-muted">{f.drugName}</div>}
                        {f.components.length > 0 && <div className="text-sm text-ink-soft">{contentsLabel(f)}</div>}
                        {f.notes && <div className="text-sm text-wait">{f.notes}</div>}
                      </td>
                      <td className="text-sm whitespace-nowrap text-ink-soft">
                        {strengthLabel(f)}
                        {f.vialSizeMl !== null && <div className="text-muted">{new Intl.NumberFormat("en-US").format(f.vialSizeMl)} mL vial</div>}
                      </td>
                      <td className="hidden text-right text-sm whitespace-nowrap text-muted sm:table-cell">
                        {usedBy(f.id) ? `In ${usedBy(f.id)} recipe${usedBy(f.id) === 1 ? "" : "s"}` : "Not used"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        );
      })}
      {manage && (
        <div>
          <Button variant="primary" onClick={() => setEditing("new")}>
            Add formulary item
          </Button>
        </div>
      )}
      {editing && <FormularyDialog item={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function FormularyDialog({ item, onClose }: { item: FormularyItem | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [v, setV] = useState<FormularyInput>(
    item
      ? {
          name: item.name,
          drugName: item.drugName,
          kind: item.kind,
          doseUnit: item.doseUnit,
          strengthPerMl: item.strengthPerMl,
          vialSizeMl: item.vialSizeMl,
          color: item.color,
          reorderAt: item.reorderAt,
          notes: item.notes,
          active: item.active,
          clinicalOnly: item.clinicalOnly,
          components: item.components,
        }
      : { name: "", drugName: "", kind: "medication", doseUnit: "mg", strengthPerMl: null, vialSizeMl: null, color: null, reorderAt: null, notes: "", active: true, clinicalOnly: false, components: [] },
  );
  const set = <K extends keyof FormularyInput>(k: K, value: FormularyInput[K]) => setV((cur) => ({ ...cur, [k]: value }));
  const save = useMutation({
    mutationFn: () => (item ? put(`/api/formulary/${item.id}`, v) : post("/api/formulary", v)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["catalog"] });
      onClose();
    },
  });

  return (
    <Dialog
      title={item ? `Edit ${item.name}` : "Add formulary item"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="formulary-form" busy={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form
        id="formulary-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
        <Field label="Name" required maxLength={80} autoFocus value={v.name} onChange={(e) => set("name", e.target.value)} />
        <Field label="Drug name or contents" maxLength={160} value={v.drugName} onChange={(e) => set("drugName", e.target.value)} />
        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Kind"
            value={v.kind}
            onChange={(e) => {
              const kind = e.target.value as FormularyKind;
              setV((cur) => ({ ...cur, kind, doseUnit: kind === "medication" ? cur.doseUnit : "each" }));
            }}
          >
            <option value="medication">Medication</option>
            <option value="fluid">Fluid</option>
            <option value="supply">Supply</option>
          </SelectField>
          <SelectField label="Measured in" value={v.doseUnit} onChange={(e) => set("doseUnit", e.target.value as DoseUnit)} hint={item ? "Can't change once used in a recipe." : undefined}>
            {DOSE_UNITS.map((u) => (
              <option key={u} value={u}>
                {u === "each" ? "Each (count)" : u === "ml" ? "mL (compounded or premixed)" : DOSE_UNIT_LABELS[u]}
              </option>
            ))}
          </SelectField>
        </div>
        {needsStrength(v.doseUnit) && (
          <Field
            label={`Strength (${DOSE_UNIT_LABELS[v.doseUnit]} per mL)`}
            type="number"
            required
            min={0}
            step="any"
            value={v.strengthPerMl ?? ""}
            onChange={(e) => set("strengthPerMl", e.target.value === "" ? null : Number(e.target.value))}
            hint="From the vial you stock. Changing it updates every build sheet."
          />
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          {v.doseUnit !== "each" ? (
            <Field
              label={v.kind === "fluid" ? "Bag size (mL)" : "Vial size (mL)"}
              type="number"
              min={0}
              step="any"
              value={v.vialSizeMl ?? ""}
              onChange={(e) => set("vialSizeMl", e.target.value === "" ? null : Number(e.target.value))}
              hint="The usual size you stock; filled in when receiving."
            />
          ) : (
            <div />
          )}
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-ink-soft">
            Colour on the inventory shelf
            <span className="flex items-center gap-2">
              <input
                type="color"
                aria-label="Colour"
                className="h-9 w-14 cursor-pointer rounded-[var(--radius-control)] border border-line bg-white p-1"
                value={vialColor(v.name || "?", v.color)}
                onChange={(e) => set("color", e.target.value.toUpperCase())}
              />
              {v.color && (
                <Button variant="quiet" onClick={() => set("color", null)}>
                  Automatic
                </Button>
              )}
            </span>
          </label>
        </div>
        <Field
          label={`Low stock at (${v.doseUnit === "each" ? "items" : v.kind === "fluid" ? "bags" : "vials"})`}
          type="number"
          min={0}
          step="any"
          className="sm:max-w-xs"
          value={v.reorderAt ?? ""}
          onChange={(e) => set("reorderAt", e.target.value === "" ? null : Number(e.target.value))}
          hint="Flagged on the shelf and the home page when usable stock is at or below this. Blank for no alert."
        />
        {v.doseUnit === "ml" && v.kind === "medication" && (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-semibold text-ink-soft">Contents per mL (for compounded products)</legend>
            {v.components.length === 0 && <p className="text-sm text-muted">Optional. List what each mL contains so build sheets and charts show it.</p>}
            {v.components.map((c, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <input
                  aria-label="Ingredient"
                  required
                  placeholder="Ingredient"
                  className="min-w-40 flex-1 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5"
                  value={c.name}
                  onChange={(e) => set("components", v.components.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                />
                <input
                  aria-label="Amount per mL"
                  type="number"
                  step="any"
                  min={0}
                  required
                  className="w-24 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5"
                  value={Number.isFinite(c.amountPerMl) ? c.amountPerMl : ""}
                  onChange={(e) => set("components", v.components.map((x, j) => (j === i ? { ...x, amountPerMl: Number(e.target.value) } : x)))}
                />
                <select
                  aria-label="Unit"
                  className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5"
                  value={c.unit}
                  onChange={(e) => set("components", v.components.map((x, j) => (j === i ? { ...x, unit: e.target.value as ComponentUnit } : x)))}
                >
                  {COMPONENT_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {DOSE_UNIT_LABELS[u]}
                    </option>
                  ))}
                </select>
                <span className="text-sm text-muted">per mL</span>
                <RemoveButton label="Remove ingredient" onClick={() => set("components", v.components.filter((_, j) => j !== i))} />
              </div>
            ))}
            <AddButton onClick={() => set("components", [...v.components, { name: "", amountPerMl: NaN, unit: "mg" }])}>Add ingredient</AddButton>
          </fieldset>
        )}
        <TextAreaField label="Notes (optional)" maxLength={500} value={v.notes} onChange={(e) => set("notes", e.target.value)} />
        <Checkbox
          label="Used in care, not sold"
          description="E.g. lidocaine for an IV start. Left out of the print menu's stock check."
          checked={v.clinicalOnly}
          onChange={(on) => set("clinicalOnly", on)}
        />
        {item && <Checkbox label="Active" description="Inactive items can't be added to new recipes." checked={v.active} onChange={(on) => set("active", on)} />}
      </form>
    </Dialog>
  );
}
